import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS, type Permission } from '@ithelpdesk/shared';
import { useTicketList } from '../api/queries';
import { useSession } from '../api/session';
import { sessionPermissionSet } from '../api/session-permissions';
import { useDebouncedValue } from '../features/tickets/useDebouncedValue';
import { cx } from '../ui/cx';
import { useFocusTrap } from '../ui/hooks';
import { Spinner } from '../ui/Spinner/Spinner';
import { StatusBadge } from '../ui/StatusBadge/StatusBadge';
import { KeyboardReturnIcon, SearchIcon, TicketsIcon } from './icons';
import { isNavItemVisible, PALETTE_ITEMS, type NavFlags, type NavItem } from './nav';
import styles from './CommandPalette.module.css';

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Same server-derived flags the sidebar gets, so both list the same pages. */
  navFlags?: NavFlags;
}

type Option =
  | { kind: 'nav'; id: string; label: string; to: string; Icon: NavItem['icon']; hint?: string }
  | { kind: 'ticket'; id: string; to: string; ticketNumber: string; title: string; requester: string | null; status: Parameters<typeof StatusBadge>[0] }
  | { kind: 'search'; id: string; to: string; query: string };

const MIN_QUERY = 2;

/**
 * Global search / command palette (Ctrl+K, ⌘K or "/"). Two sources:
 *
 *  - Navigate: "New Ticket" plus the same nav entries the sidebar shows for this actor
 *    (nav.ts#isNavItemVisible — UX only, every route re-checks server-side).
 *  - Tickets: `GET /tickets?q=` — the existing server-side full-text filter
 *    (title, description, ticket number, requester name/email). Visibility
 *    is enforced by the API's actor-scoped query; this component never
 *    filters or widens what the server returns.
 *
 * ARIA combobox pattern: the input owns a listbox and points
 * `aria-activedescendant` at the highlighted option.
 */
export function CommandPalette({ open, onClose, navFlags }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const listId = useId();
  const { data: session } = useSession();

  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  const perms: Set<Permission> = sessionPermissionSet(session);
  const isStaff = perms.has(PERMISSIONS.TICKET_READ_TEAM) || perms.has(PERMISSIONS.TICKET_READ_ANY);
  const trimmed = query.trim();
  const debounced = useDebouncedValue(trimmed, 200);
  const searching = open && debounced.length >= MIN_QUERY;

  const tickets = useTicketList(
    { q: debounced || undefined, limit: 8, sort: 'updatedAt', order: 'desc' },
    { enabled: searching, keepPrevious: true },
  );

  const options = useMemo<Option[]>(() => {
    const lower = trimmed.toLowerCase();
    const navOptions: Option[] = PALETTE_ITEMS.filter((item) => isNavItemVisible(item, perms, navFlags))
      .filter((item) => !lower || item.label.toLowerCase().includes(lower))
      .slice(0, lower ? 6 : 8)
      .map((item) => ({
        kind: 'nav',
        id: `nav-${item.to}`,
        label: item.label,
        to: item.to,
        Icon: item.icon,
        ...(item.group === 'admin' ? { hint: 'Admin' } : {}),
      }));
    // Only results for exactly what's typed now — never a previous query's
    // placeholder page, or Enter could open the wrong ticket.
    const fresh = searching && trimmed === debounced && !tickets.isPlaceholderData;
    const ticketOptions: Option[] =
      fresh && tickets.data
        ? tickets.data.data.map((t) => ({
            kind: 'ticket',
            id: `ticket-${t.id}`,
            to: `/tickets/${t.id}`,
            ticketNumber: t.ticketNumber,
            title: t.title,
            requester: t.requester?.displayName ?? null,
            status: { status: t.status },
          }))
        : [];
    const searchAll: Option[] =
      trimmed.length >= MIN_QUERY
        ? [
            {
              kind: 'search',
              id: 'search-all',
              to: `${isStaff ? '/tickets' : '/my-tickets'}?q=${encodeURIComponent(trimmed)}`,
              query: trimmed,
            },
          ]
        : [];
    return [...ticketOptions, ...searchAll, ...navOptions];
    // perms is derived from session; session identity is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, debounced, searching, tickets.data, tickets.isPlaceholderData, session, isStaff, navFlags]);

  useEffect(() => {
    setActive((i) => (options.length === 0 ? 0 : Math.min(i, options.length - 1)));
  }, [options.length]);

  if (!open) return null;

  function choose(option: Option | undefined): void {
    if (!option) return;
    onClose();
    navigate(option.to);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (options.length ? (i + 1) % options.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (options.length ? (i - 1 + options.length) % options.length : 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(Math.max(0, options.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(options[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  const activeOption = options[active];
  const ticketCount = options.filter((o) => o.kind === 'ticket').length;
  const firstNavIndex = options.findIndex((o) => o.kind === 'nav');
  const loading = searching && tickets.isFetching;
  const noTicketResults = searching && !tickets.isFetching && tickets.data && ticketCount === 0;

  return (
    <div className={styles.backdrop} onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Search and navigate"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.inputRow}>
          <SearchIcon className={styles.searchIcon} />
          <input
            ref={inputRef}
            className={styles.input}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeOption ? `${listId}-${activeOption.id}` : undefined}
            aria-label="Search tickets or jump to a page"
            placeholder={isStaff ? 'Search tickets by number, subject or requester…' : 'Search your tickets…'}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <Spinner size="sm" label="Searching" />}
          <kbd className={styles.escKey}>Esc</kbd>
        </div>

        <ul id={listId} role="listbox" aria-label="Results" className={styles.list}>
          {options.map((option, index) => {
            const selected = index === active;
            const common = {
              id: `${listId}-${option.id}`,
              role: 'option' as const,
              'aria-selected': selected,
              className: cx(styles.option, selected && styles.optionActive),
              onMouseMove: () => setActive(index),
              onMouseDown: (e: ReactMouseEvent) => e.preventDefault(),
              onClick: () => choose(option),
            };
            const groupHeader =
              index === 0 && option.kind === 'ticket' ? (
                <li role="presentation" className={styles.groupLabel} key="g-tickets">
                  Tickets
                </li>
              ) : index === firstNavIndex ? (
                <li role="presentation" className={styles.groupLabel} key="g-nav">
                  Go to
                </li>
              ) : null;

            let body;
            if (option.kind === 'nav') {
              const Icon = option.Icon;
              body = (
                <>
                  <Icon className={styles.optionIcon} />
                  <span className={styles.optionTitle}>{option.label}</span>
                  {option.hint && <span className={styles.optionHint}>{option.hint}</span>}
                </>
              );
            } else if (option.kind === 'ticket') {
              body = (
                <>
                  <TicketsIcon className={styles.optionIcon} />
                  <span className={styles.ticketNumber}>{option.ticketNumber}</span>
                  <span className={styles.optionTitle}>{option.title}</span>
                  {option.requester && <span className={styles.optionHint}>{option.requester}</span>}
                  <StatusBadge {...option.status} />
                </>
              );
            } else {
              body = (
                <>
                  <SearchIcon className={styles.optionIcon} />
                  <span className={styles.optionTitle}>
                    See all results for “<strong>{option.query}</strong>”
                  </span>
                </>
              );
            }
            return [
              groupHeader,
              <li key={option.id} {...common}>
                {body}
                {selected && <KeyboardReturnIcon className={styles.enterIcon} aria-hidden="true" />}
              </li>,
            ];
          })}
        </ul>

        {noTicketResults && <p className={styles.empty}>No tickets match “{debounced}”.</p>}
        {searching && tickets.isError && (
          <p className={styles.empty} role="alert">
            Ticket search is unavailable right now. You can still jump to a page below, or try again in a moment.
          </p>
        )}

        <div className={styles.footer} aria-hidden="true">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> to move
          </span>
          <span>
            <kbd>Enter</kbd> to open
          </span>
          <span>
            <kbd>Esc</kbd> to close
          </span>
        </div>
      </div>
    </div>
  );
}
